import { Injectable } from '@angular/core';
import {
  DailyLogOperationInstance,
  DailyLogType,
  DailyOperationLogRecord,
  LogComment
} from '../../models/projects/operation.models';
import { OperationService } from '../operations/operation/operation.service';
import { DailyLogService } from '../daily-log/daily-log.service';
import { DateParserService } from '../daily-log/daily-log-time/date-parser-service';
import { AppDataSummary } from '../../models/app-data/app-data';
import { OperationTimeService } from '../operations/operation-time/operation-time.service';

@Injectable()
export class RecordOperationLinkService {

  constructor(
    private operationService: OperationService,
    private dailyLogService: DailyLogService,
    private dateParserService: DateParserService,
    private operationTimeService: OperationTimeService) {
  }

  async updateLogs(instance: DailyLogOperationInstance): Promise<DailyOperationLogRecord[]> {
    let logs = await this.dailyLogService.getDailyLogDataForOperation(instance.id);

    logs = await this.dateParserService.deserializeDates(logs);

    const startLog = logs.find(p => p.typeId === DailyLogType.OperationStart);

    startLog.startTime = instance.startTime;
    startLog.sortTime = !instance.startTime ? startLog.sortTime : instance.startTime.dateTime;
    startLog.startTimeShow = instance.startTimeToShow;
    startLog.startTimeConfirmed = instance.startConfirmed;

    const stepLogs = logs.filter(p => p.typeId === DailyLogType.Step);

    stepLogs.forEach(log => {
      const step = instance.stepCollectionInstance.stepInstances.find(p => p.number === log.stepNumber);

      if (!step) {
        return;
      }

      log.startTime = step.startTime;
      log.startTimeShow = step.startTimeToShow;
      log.endTime = step.endTime;
      log.endTimeShow = step.endTimeToShow;
      log.startTimeConfirmed = step.startConfirmed;
      log.endTimeConfirmed = step.endConfirmed;
      log.archived = (step.exclude || (!step.endTime && !step.startTime));
      log.sortTime = !step.startTime ? log.sortTime : step.startTime.dateTime;
    });

    const endLog = logs.find(p => p.typeId === DailyLogType.OperationEnd);

    endLog.endTime = instance.endTime;
    endLog.sortTime = !instance.endTime ? endLog.sortTime : instance.endTime.dateTime;
    endLog.endTimeShow = instance.endTimeToShow;
    endLog.endTimeConfirmed = instance.endConfirmed;

    const commentLogs = logs.filter(p => p.typeId === DailyLogType.Comment);

    commentLogs.forEach(log => {
      const comment = instance.comments.find(p => p.identifier === log.commentIdentifier);

      if (!comment) {
        return;
      }

      log.startTimeShow = comment.startTimeToShow;
      log.endTimeShow = comment.endTimeToShow;
    });

    await this.dailyLogService.saveDailyLogData([startLog]);
    await this.dailyLogService.saveDailyLogData(stepLogs);
    await this.dailyLogService.saveDailyLogData([endLog]);
    await this.dailyLogService.saveDailyLogData(commentLogs);

    return logs;
  }

  async updateOperations(records: DailyOperationLogRecord[]): Promise<DailyLogOperationInstance[]> {
    const instances = [] as DailyLogOperationInstance[];

    for (let i = 0; i < records.length; i++) {
      let instance = instances.find(p => records[i].operationInstanceId
        ? p.id === records[i].operationInstanceId
        : p.localId === records[i].operationInstanceLocalId);

      if (!instance) {
        instance = await this.operationService
          .getProjectSpecificOperationInstance(records[i].operationInstanceId
            ? records[i].operationInstanceId
            : records[i].operationInstanceLocalId);
      }

      if (!instance) {

      } else {
        const tempInstance = instances.find(e => instance.id
          ? e.id === instance.id
          : e.localId === instance.localId);
        if (!tempInstance) {
          instances.push(instance);
        } else {
          instance = tempInstance;
        }
      }
    }

    for (let i = 0; i < instances.length; i++) {
      const instanceDailyLogRecords = await this.dailyLogService.getDailyLogDataForOperation(instances[i].id ? instances[i].id : instances[i].localId);

      for (let j = 0; j < instanceDailyLogRecords.length; j++) {
        // if the record exists in the updated records, then use it so that we get updated times for records that have been edited
        // (times on the instanceDailyLogRecords array are not updated - but we still need them or the instance times will be wrong in the edit screen)

        const updatedRecords = records
          .filter(e => instanceDailyLogRecords[j].id
            ? e.id === instanceDailyLogRecords[j].id
            : e.localId === instanceDailyLogRecords[j].localId);
        let chosenRecord = instanceDailyLogRecords[j];
        if (updatedRecords && updatedRecords[0]) {
          chosenRecord = updatedRecords[0];
        }

        await this.dailyLogService.setDailyLogOperationData(chosenRecord, instances[i]);
      }

      await this.operationService
        .localSaveProjectSpecificOperationInstance(instances[i].id
          ? instances[i].id
          : instances[i].localId, instances[i]);
    }

    return instances;
  }

  checkDeletedComments(originalComments: LogComment[],
                       updatedComments: LogComment[],
                       operationInstanceId: number,
                       deletionData: AppDataSummary
  ): AppDataSummary {
    if (!!originalComments && !!updatedComments) {
      originalComments.forEach(originalComment => {
        if (updatedComments.findIndex(e => originalComment.recordApiId != null
          ? e.recordApiId === originalComment.recordApiId
          : originalComment.recordLocalId != null
            ? e.recordLocalId === originalComment.recordLocalId
            : e.identifier === originalComment.identifier) === -1) {
          deletionData = this.deleteCommentFromDailyLog(originalComment, operationInstanceId, deletionData);
        }
      });
    }

    return deletionData;
  }

  deleteCommentFromDailyLog(comment: LogComment, operationInstanceId: number, deletionData: AppDataSummary): AppDataSummary {
    this.dailyLogService.getDailyLogData().then(data => {
      // @ts-ignore
      const index = data.findIndex(e => (e.typeId === DailyLogType.Comment || e.typeId === 8)
        && (e != null && e.comment != null)
        && (comment.recordApiId != null
          ? e.comment.recordApiId === comment.recordApiId
          : e.comment.recordLocalId != null
            ? e.comment.recordLocalId === comment.recordLocalId
            : e.comment.identifier === comment.identifier)
        && (e.operationInstanceId != null
          ? e.operationInstanceId === operationInstanceId
          : e.operationInstanceLocalId === operationInstanceId));
      if (index !== -1) {
        deletionData.dailyLogRecordIds.push(data[index].id ? data[index].id : data[index].localId);
      }
    });

    return deletionData;
  }

  deleteCommentFromOperation(record: DailyOperationLogRecord) {
    this.operationService
      .getProjectSpecificOperationInstance(record.operationInstanceId)
      .then(instance => {
        const index = instance.comments.findIndex(e => e.identifier === record.commentIdentifier);
        instance.comments.splice(index, 1);

        this.operationService.localSaveProjectSpecificOperationInstance(instance.id, instance);
      });
  }
}



// WEBPACK FOOTER //
// ./src/services/record-operation-link/record-operation-link.service.ts